"use client";

import { useEffect, useState, type FormEvent } from "react";
import { X } from "lucide-react";
import type { EstadoBrasil, LocalidadeUsuario, MunicipioIbge } from "@/lib/localidades-brasil";
import { Button } from "./Button";
import { Dialog } from "./Dialog";
import { Input } from "./Input";
import { LoadingIndicator } from "./LoadingIndicator";
import { Select } from "./Select";

type RespostaLocalidade = { localidade: LocalidadeUsuario | null; estados: EstadoBrasil[]; erro?: string };
type RespostaMunicipios = { municipios: MunicipioIbge[]; erro?: string };
type RespostaSalvar = { ok?: boolean; localidade?: LocalidadeUsuario; erro?: string };

export function PopupConfiguracoes({ onClose }: { onClose: () => void }) {
  const [estados, setEstados] = useState<EstadoBrasil[]>([]);
  const [municipios, setMunicipios] = useState<MunicipioIbge[]>([]);
  const [estado, setEstado] = useState("");
  const [cidadeId, setCidadeId] = useState("");
  const [carregando, setCarregando] = useState(true);
  const [carregandoCidades, setCarregandoCidades] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState("");
  const [sucesso, setSucesso] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    fetch("/api/localidade", { cache: "no-store", signal: controller.signal })
      .then(async (resposta) => {
        const dados = await resposta.json().catch(() => ({})) as Partial<RespostaLocalidade>;
        if (!resposta.ok) throw new Error(dados.erro ?? "Não foi possível carregar a localidade.");
        return dados;
      })
      .then((dados) => {
        if (controller.signal.aborted) return;
        setEstados(dados.estados ?? []);
        setEstado(dados.localidade?.estado ?? "");
        setCidadeId(dados.localidade ? String(dados.localidade.cidadeId) : "");
        if (dados.localidade) setCarregandoCidades(true);
      })
      .catch((falha) => {
        if (!controller.signal.aborted) setErro(falha instanceof Error ? falha.message : "Não foi possível carregar a localidade.");
      })
      .finally(() => { if (!controller.signal.aborted) setCarregando(false); });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    if (!estado) return;
    const controller = new AbortController();
    fetch(`/api/localidade?estado=${estado}`, { cache: "no-store", signal: controller.signal })
      .then(async (resposta) => {
        const dados = await resposta.json().catch(() => ({})) as Partial<RespostaMunicipios>;
        if (!resposta.ok) throw new Error(dados.erro ?? "Não foi possível carregar as cidades.");
        return dados;
      })
      .then((dados) => {
        if (controller.signal.aborted) return;
        const cidades = dados.municipios ?? [];
        setMunicipios(cidades);
        setCidadeId((atual) => cidades.some((cidade) => String(cidade.id) === atual) ? atual : "");
      })
      .catch((falha) => {
        if (!controller.signal.aborted) setErro(falha instanceof Error ? falha.message : "Não foi possível carregar as cidades.");
      })
      .finally(() => { if (!controller.signal.aborted) setCarregandoCidades(false); });
    return () => controller.abort();
  }, [estado]);

  function selecionarEstado(novoEstado: string) {
    setEstado(novoEstado);
    setCidadeId("");
    setMunicipios([]);
    setCarregandoCidades(Boolean(novoEstado));
    setErro("");
    setSucesso("");
  }

  async function salvar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (salvando || !estado || !municipios.some((cidade) => String(cidade.id) === cidadeId)) return;
    setSalvando(true);
    setErro("");
    setSucesso("");
    try {
      const resposta = await fetch("/api/localidade", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ estado, cidadeId: Number(cidadeId) }),
      });
      const dados = await resposta.json().catch(() => ({})) as RespostaSalvar;
      if (!resposta.ok || dados.ok !== true || !dados.localidade) {
        throw new Error(dados.erro ?? "Não foi possível salvar a localidade.");
      }
      setEstado(dados.localidade.estado);
      setCidadeId(String(dados.localidade.cidadeId));
      setSucesso("Localidade salva.");
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : "Não foi possível salvar a localidade.");
    } finally {
      setSalvando(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(aberto) => { if (!aberto && !salvando) onClose(); }}
      closeOnEscape={!salvando}
      closeOnBackdrop={!salvando}
      aria-labelledby="titulo-popup-configuracoes"
    >
      <div className="flex items-center justify-between gap-3">
        <h2 id="titulo-popup-configuracoes" className="text-xl font-semibold">Configurações</h2>
        <Button variant="ghost" size="icon" aria-label="Fechar Configurações" onClick={onClose} disabled={salvando}><X className="h-5 w-5" /></Button>
      </div>

      {carregando ? <LoadingIndicator className="py-8">Carregando Configurações...</LoadingIndicator> : (
        <form onSubmit={(evento) => void salvar(evento)} className="mt-5 space-y-5">
          <div className="space-y-3">
            <h3 className="font-semibold">Localidade</h3>
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="block space-y-1.5 text-sm font-medium">Estado
                <Select value={estado} onChange={(evento) => selecionarEstado(evento.target.value)} disabled={salvando || estados.length === 0} required>
                  <option value="">Selecione o Estado</option>
                  {estados.map((item) => <option key={item.sigla} value={item.sigla}>{item.nome}</option>)}
                </Select>
              </label>
              <label className="block space-y-1.5 text-sm font-medium">Cidade
                <Select value={cidadeId} onChange={(evento) => { setCidadeId(evento.target.value); setErro(""); setSucesso(""); }} disabled={!estado || carregandoCidades || salvando} required>
                  <option value="">{carregandoCidades ? "Carregando cidades..." : "Selecione a Cidade"}</option>
                  {municipios.map((cidade) => <option key={cidade.id} value={cidade.id}>{cidade.nome}</option>)}
                </Select>
              </label>
            </div>
            <Button type="submit" disabled={salvando || carregandoCidades || !estado || !municipios.some((cidade) => String(cidade.id) === cidadeId)}>
              {salvando ? "Salvando..." : "Salvar localidade"}
            </Button>
          </div>
          <label className="block space-y-1.5 text-sm font-medium">Idioma
            <Input value="Português (Brasil)" readOnly aria-readonly="true" />
            <span className="block text-xs font-normal text-[var(--fz-field-disabled-text)]">Idioma fixo; não pode ser alterado.</span>
          </label>
        </form>
      )}
      {erro ? <p role="alert" className="mt-4 text-sm text-[var(--fz-field-error)]">{erro}</p> : null}
      {sucesso ? <p role="status" className="mt-4 text-sm">{sucesso}</p> : null}
    </Dialog>
  );
}
